import React, { useCallback, useEffect, useMemo, useState } from 'react'
import Cropper from 'react-easy-crop'
import { Button, Dialog } from 'components/ui'
import { HiCheck, HiRefresh, HiX } from 'react-icons/hi'
import 'react-easy-crop/react-easy-crop.css'

const createImage = (url) =>
    new Promise((resolve, reject) => {
        const image = new Image()
        image.addEventListener('load', () => resolve(image))
        image.addEventListener('error', (error) => reject(error))
        image.setAttribute('crossOrigin', 'anonymous')
        image.src = url
    })

const getCroppedImage = async (imageSrc, croppedAreaPixels) => {
    const image = await createImage(imageSrc)
    const canvas = document.createElement('canvas')
    const ctx = canvas.getContext('2d')

    if (!ctx) {
        throw new Error('No 2d context')
    }

    canvas.width = croppedAreaPixels.width
    canvas.height = croppedAreaPixels.height

    ctx.drawImage(
        image,
        croppedAreaPixels.x,
        croppedAreaPixels.y,
        croppedAreaPixels.width,
        croppedAreaPixels.height,
        0,
        0,
        croppedAreaPixels.width,
        croppedAreaPixels.height
    )

    return new Promise((resolve) => {
        canvas.toBlob(resolve, 'image/jpeg', 0.92)
    })
}

const formatRatioLabel = (preset) => preset?.label || 'Free'

const ImageCropper = ({
    isOpen,
    onClose,
    imageSrc,
    onCropComplete,
    aspectRatio = null,
    title = 'Crop Image',
    cropPresets = [],
    originalFile = null,
}) => {
    const [crop, setCrop] = useState({ x: 0, y: 0 })
    const [zoom, setZoom] = useState(1)
    const [rotation, setRotation] = useState(0)
    const [currentAspectRatio, setCurrentAspectRatio] = useState(aspectRatio)
    const [croppedAreaPixels, setCroppedAreaPixels] = useState(null)
    const [isApplying, setIsApplying] = useState(false)

    const presets = useMemo(() => {
        if (cropPresets?.length) {
            return cropPresets
        }

        return [
            { label: 'Free', ratio: null },
            { label: 'Square', ratio: 1 },
            { label: 'Portrait', ratio: 3 / 4 },
            { label: 'Landscape', ratio: 4 / 3 },
            { label: 'Widescreen', ratio: 16 / 9 },
        ]
    }, [cropPresets])

    useEffect(() => {
        if (!isOpen) {
            return
        }

        setCrop({ x: 0, y: 0 })
        setZoom(1)
        setRotation(0)
        setCurrentAspectRatio(aspectRatio)
        setCroppedAreaPixels(null)
        setIsApplying(false)
    }, [aspectRatio, imageSrc, isOpen])

    const handleCropComplete = useCallback((_, croppedPixels) => {
        setCroppedAreaPixels(croppedPixels)
    }, [])

    const handleApplyCrop = useCallback(async () => {
        if (!imageSrc || !croppedAreaPixels) {
            return
        }

        try {
            setIsApplying(true)
            const croppedBlob = await getCroppedImage(imageSrc, croppedAreaPixels)
            if (!croppedBlob) {
                throw new Error('Unable to create cropped image')
            }
            onCropComplete(croppedBlob)
            onClose()
        } catch (error) {
            // Keep failure silent here; existing upload flows already surface errors downstream.
            console.error('Error cropping image:', error)
        } finally {
            setIsApplying(false)
        }
    }, [croppedAreaPixels, imageSrc, onClose, onCropComplete])

    const handleSkipCrop = useCallback(() => {
        if (onCropComplete && originalFile) {
            onCropComplete(originalFile)
        }
        onClose()
    }, [onClose, onCropComplete, originalFile])

    const resetCrop = useCallback(() => {
        setCrop({ x: 0, y: 0 })
        setZoom(1)
        setRotation(0)
        setCroppedAreaPixels(null)
    }, [])

    return (
        <Dialog
            isOpen={isOpen}
            onClose={() => {}}
            onRequestClose={() => {}}
            closable={false}
            width={980}
            height="auto"
        >
            <div className="flex items-center justify-between border-b border-gray-200 bg-white px-7 py-5">
                <div>
                    <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-gray-400">
                        Image Editor
                    </p>
                    <h4 className="mt-1 text-xl font-semibold text-gray-900">{title}</h4>
                    <p className="mt-1 text-sm text-gray-500">
                        Pan and zoom to frame the image exactly as you want it to appear.
                    </p>
                </div>
                <div className="flex gap-2">
                    <Button
                        size="sm"
                        variant="plain"
                        icon={<HiRefresh />}
                        onClick={resetCrop}
                        title="Reset crop"
                    />
                    <Button
                        size="sm"
                        variant="plain"
                        icon={<HiX />}
                        onClick={onClose}
                        title="Close modal"
                        className="text-gray-500 hover:text-gray-700"
                    />
                </div>
            </div>

            <div className="max-h-[78vh] overflow-y-auto bg-[#f6f4ef] px-7 py-6">
                <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_280px]">
                    <div className="overflow-hidden rounded-[28px] border border-black/5 bg-[#111111] shadow-[0_24px_60px_rgba(15,23,42,0.18)]">
                        <div className="border-b border-white/10 px-5 py-3 text-xs font-medium uppercase tracking-[0.22em] text-white/50">
                            Live Crop Preview
                        </div>
                        <div className="relative h-[560px] w-full">
                            {imageSrc ? (
                                <Cropper
                                    image={imageSrc}
                                    crop={crop}
                                    zoom={zoom}
                                    rotation={rotation}
                                    aspect={currentAspectRatio || undefined}
                                    objectFit="contain"
                                    showGrid
                                    zoomSpeed={0.15}
                                    minZoom={1}
                                    maxZoom={4}
                                    cropShape="rect"
                                    onCropChange={setCrop}
                                    onZoomChange={setZoom}
                                    onRotationChange={setRotation}
                                    onCropComplete={handleCropComplete}
                                />
                            ) : null}
                        </div>
                    </div>

                    <div className="space-y-5 rounded-[28px] border border-black/5 bg-white p-5 shadow-[0_16px_40px_rgba(15,23,42,0.08)]">
                        <div>
                            <p className="mb-1 text-[11px] font-semibold uppercase tracking-[0.2em] text-gray-400">
                                Composition
                            </p>
                            <label className="mb-3 block text-sm font-semibold text-gray-900">
                                Crop ratio
                            </label>
                            <div className="flex flex-wrap gap-2">
                                {presets.map((preset, index) => {
                                    const active = currentAspectRatio === preset.ratio
                                    return (
                                        <Button
                                            key={`${preset.label}-${index}`}
                                            size="sm"
                                            variant={active ? 'solid' : 'plain'}
                                            color={active ? 'gray-800' : undefined}
                                            onClick={() => setCurrentAspectRatio(preset.ratio)}
                                            className={active ? 'shadow-sm' : 'border border-gray-200'}
                                        >
                                            {formatRatioLabel(preset)}
                                        </Button>
                                    )
                                })}
                            </div>
                        </div>

                        <div>
                            <div className="mb-2 flex items-center justify-between">
                                <label className="text-sm font-semibold text-gray-900">
                                    Zoom
                                </label>
                                <span className="text-xs font-medium text-gray-500">
                                    {zoom.toFixed(2)}x
                                </span>
                            </div>
                            <input
                                className="w-full accent-black"
                                type="range"
                                min="1"
                                max="4"
                                step="0.01"
                                value={zoom}
                                onChange={(event) => setZoom(Number(event.target.value))}
                            />
                        </div>

                        <div>
                            <div className="mb-2 flex items-center justify-between">
                                <label className="text-sm font-semibold text-gray-900">
                                    Rotation
                                </label>
                                <span className="text-xs font-medium text-gray-500">
                                    {rotation}°
                                </span>
                            </div>
                            <input
                                className="w-full accent-black"
                                type="range"
                                min="0"
                                max="360"
                                step="1"
                                value={rotation}
                                onChange={(event) =>
                                    setRotation(Number(event.target.value))
                                }
                            />
                        </div>

                        <div className="rounded-2xl border border-[#e9e2d3] bg-[#fbf8f2] p-4">
                            <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-[#9a8663]">
                                Guidance
                            </p>
                            <p className="mt-2 text-sm leading-6 text-gray-600">
                                Drag the image to reposition it inside the frame. Use zoom for
                                precision, and only change ratio when the final placement actually
                                needs a different crop.
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            <div className="flex justify-between gap-3 border-t border-gray-200 bg-white px-7 py-5">
                <Button
                    variant="plain"
                    onClick={handleSkipCrop}
                    className="text-gray-600 hover:text-gray-800"
                >
                    Use Original
                </Button>
                <div className="flex gap-3">
                    <Button variant="plain" onClick={onClose} icon={<HiX />}>
                        Cancel
                    </Button>
                    <Button
                        variant="solid"
                        color="gray-800"
                        onClick={handleApplyCrop}
                        loading={isApplying}
                        disabled={!croppedAreaPixels}
                        icon={<HiCheck />}
                    >
                        Apply Crop
                    </Button>
                </div>
            </div>
        </Dialog>
    )
}

export default ImageCropper
